import React, { useState } from 'react';
import { ListChecks, LocateFixed, Plus, RefreshCw, Sparkles, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Tooltip } from '../ui/tooltip';
import type { AddPlanConstraintInput, PlanAiProposalResult, PlanConstraintInfo, StaffingPlanBoard } from './types';
import { PlanAiBox } from './PlanAiBox';
import { BoardModel, exceptionTargets } from './boardModel';
import { ExceptionMark } from './PlanBoard';
import { PlanConstraintForm } from './PlanConstraintForm';
import { PlanConstraintTypeLabel } from './planConstraintLabels';
import { PlanConfirmDialog } from './PlanConfirmDialog';

interface PlanConstraintsPanelProps {
  board: StaffingPlanBoard;
  model: BoardModel;
  canEdit: boolean;
  busy: boolean;
  /** True after exceptions changed and the plan was not re-laid since. */
  needsRegenerate: boolean;
  onAdd: (input: Omit<AddPlanConstraintInput, 'planId'>) => Promise<boolean>;
  onDelete: (constraint: PlanConstraintInfo) => Promise<boolean>;
  onConfirm: (constraint: PlanConstraintInfo) => void;
  onReject: (constraint: PlanConstraintInfo) => void;
  /** Rejects every alternative of one AI sentence. */
  onRejectAll: (constraints: PlanConstraintInfo[]) => void;
  onRegenerate: () => void;
  /** Show the AI text box (AI configured on the server and user has planning.ai). */
  aiEnabled: boolean;
  onPropose: (text: string) => Promise<PlanAiProposalResult | null>;
  /** Highlights the board places of a confirmed exception (null = clear). */
  onHighlight: (constraint: PlanConstraintInfo | null) => void;
  /** Scrolls the board to the places of a confirmed exception. */
  onLocate: (constraint: PlanConstraintInfo) => void;
}

const AiMark: React.FC = () => (
  <Tooltip content="Wyjątek zaproponowany na podstawie wpisanego tekstu" side="top">
    <span className="inline-flex cursor-help items-center gap-0.5 rounded border border-violet-300 bg-violet-100 px-1 text-[10px] font-semibold text-violet-800 dark:border-violet-700 dark:bg-violet-950/60 dark:text-violet-200">
      <Sparkles className="h-3 w-3" /> AI
    </span>
  </Tooltip>
);

export const PlanConstraintsPanel: React.FC<PlanConstraintsPanelProps> = ({
  board,
  model,
  canEdit,
  busy,
  needsRegenerate,
  onAdd,
  onDelete,
  onConfirm,
  onReject,
  onRejectAll,
  onRegenerate,
  aiEnabled,
  onPropose,
  onHighlight,
  onLocate,
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState<PlanConstraintInfo | null>(null);

  const confirmed = board.constraints.filter((c) => c.status === 'CONFIRMED');
  const proposed = board.constraints.filter((c) => c.status === 'PROPOSED');

  // Alternatives of one sentence share alternativeGroupId; keep the original order.
  const proposalGroups: PlanConstraintInfo[][] = [];
  const groupIndex = new Map<string, number>();
  for (const c of proposed) {
    if (c.alternativeGroupId) {
      const idx = groupIndex.get(c.alternativeGroupId);
      if (idx !== undefined) {
        proposalGroups[idx].push(c);
        continue;
      }
      groupIndex.set(c.alternativeGroupId, proposalGroups.length);
    }
    proposalGroups.push([c]);
  }

  const handleAdd = async (input: Omit<AddPlanConstraintInput, 'planId'>) => {
    const ok = await onAdd(input);
    if (ok) setFormOpen(false);
    return ok;
  };

  const renderItem = (c: PlanConstraintInfo, actions: React.ReactNode, testId = 'plan-constraint-item') => {
    // Confirmed exceptions that land on the board can be located there.
    const locatable = c.status === 'CONFIRMED' && exceptionTargets(c, board.dates).length > 0;
    const body = (
      <>
        <div className="flex flex-wrap items-center gap-1.5">
          {c.status === 'CONFIRMED' && <ExceptionMark />}
          <span className="text-xs text-muted-foreground">
            <PlanConstraintTypeLabel type={c.type} />
          </span>
          {c.origin === 'AI' && <AiMark />}
        </div>
        <div>{c.description}</div>
        {c.origin === 'AI' && c.originalText && (
          <div className="mt-0.5 text-xs italic text-muted-foreground">Wpisano: „{c.originalText}”</div>
        )}
        {locatable && (
          <div className="mt-0.5 inline-flex items-center gap-1 text-xs text-primary">
            <LocateFixed className="h-3 w-3" /> Pokaż na planie
          </div>
        )}
      </>
    );
    return (
      <li
        key={c.id}
        className="flex flex-wrap items-start justify-between gap-2 px-4 py-2.5"
        data-testid={testId}
        onMouseEnter={locatable ? () => onHighlight(c) : undefined}
        onMouseLeave={locatable ? () => onHighlight(null) : undefined}
      >
        {locatable ? (
          <button
            type="button"
            onClick={() => onLocate(c)}
            onFocus={() => onHighlight(c)}
            onBlur={() => onHighlight(null)}
            className="-mx-1 min-w-0 flex-1 rounded px-1 text-left text-sm hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="plan-constraint-locate"
          >
            {body}
          </button>
        ) : (
          <div className="min-w-0 flex-1 text-sm">{body}</div>
        )}
        {actions && <div className="flex shrink-0 gap-2">{actions}</div>}
      </li>
    );
  };

  return (
    <Card data-testid="plan-constraints">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ListChecks className="h-4 w-4 text-primary" />
              Wyjątki ({confirmed.length})
            </CardTitle>
            <CardDescription>
              Szczególne zasady na ten tydzień, np. ktoś jest niedostępny albo pracuje tylko na jednej zmianie.
              Program bierze je pod uwagę przy układaniu planu.
            </CardDescription>
          </div>
          {canEdit && !formOpen && (
            <Button variant="outline" size="sm" onClick={() => setFormOpen(true)} disabled={busy}>
              <Plus className="mr-2 h-4 w-4" />
              Dodaj wyjątek
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {needsRegenerate && canEdit && (
          <div
            className="flex flex-wrap items-center gap-3 rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-sm"
            data-testid="plan-regenerate-prompt"
          >
            <span className="flex-1 min-w-[16rem]">
              Wyjątki się zmieniły, ale plan nie został jeszcze ułożony na nowo. „Ułóż ponownie” zastąpi nieprzypięte
              komórki, a przypięte zostawi bez zmian.
            </span>
            <Button size="sm" onClick={onRegenerate} disabled={busy} data-testid="plan-regenerate-now">
              <RefreshCw className="mr-2 h-4 w-4" />
              Ułóż ponownie
            </Button>
          </div>
        )}

        {aiEnabled && canEdit && <PlanAiBox disabled={busy} onPropose={onPropose} />}

        {formOpen && canEdit && (
          <PlanConstraintForm
            board={board}
            model={model}
            busy={busy}
            onSubmit={handleAdd}
            onCancel={() => setFormOpen(false)}
          />
        )}

        {proposed.length > 0 && (
          <div className="rounded-md border border-violet-300 dark:border-violet-800">
            <div className="border-b border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-900 dark:border-violet-900 dark:bg-violet-950/40 dark:text-violet-100">
              Do potwierdzenia ({proposed.length}) — nie wpływają na plan, dopóki ich nie zastosujesz
            </div>
            <ul className="divide-y">
              {proposalGroups.map((group) =>
                group.length === 1 ? (
                  renderItem(
                    group[0],
                    canEdit ? (
                      <>
                        <Button size="sm" onClick={() => onConfirm(group[0])} disabled={busy}>
                          Zastosuj
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => onReject(group[0])} disabled={busy}>
                          Odrzuć
                        </Button>
                      </>
                    ) : null,
                    'plan-constraint-proposal',
                  )
                ) : (
                  <li key={group[0].alternativeGroupId!} className="px-4 py-2.5" data-testid="plan-constraint-alternatives">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-sm font-medium">
                        Wybierz właściwą osobę
                        {group[0].origin === 'AI' && <AiMark />}
                      </div>
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => onRejectAll(group)} disabled={busy}>
                          Odrzuć wszystkie
                        </Button>
                      )}
                    </div>
                    {group[0].originalText && (
                      <div className="mt-0.5 text-xs italic text-muted-foreground">Wpisano: „{group[0].originalText}”</div>
                    )}
                    <ul className="mt-2 space-y-1.5">
                      {group.map((c) => (
                        <li
                          key={c.id}
                          className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-sm"
                          data-testid="plan-constraint-alternative"
                        >
                          <span className="min-w-0 flex-1">{c.description}</span>
                          {canEdit && (
                            <Button size="sm" onClick={() => onConfirm(c)} disabled={busy}>
                              Zastosuj
                            </Button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ),
              )}
            </ul>
          </div>
        )}

        {confirmed.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak wyjątków w tym tygodniu.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {confirmed.map((c) =>
              renderItem(
                c,
                canEdit ? (
                  <Tooltip content="Usuń wyjątek" side="left">
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label="Usuń wyjątek"
                      className="text-red-600 hover:text-red-700 dark:text-red-400"
                      onClick={() => setToDelete(c)}
                      disabled={busy}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </Tooltip>
                ) : null,
              ),
            )}
          </ul>
        )}
      </CardContent>

      <PlanConfirmDialog
        open={!!toDelete}
        title="Usunąć wyjątek?"
        destructive
        description={
          <>
            <p>„{toDelete?.description}”</p>
            <p>
              Wyjątek zostanie usunięty z tego tygodnia. Plan nie zmieni się sam — żeby to uwzględnić, ułóż go ponownie.
            </p>
          </>
        }
        confirmLabel="Usuń wyjątek"
        busyLabel="Usuwanie..."
        busy={busy}
        onConfirm={async () => {
          if (!toDelete) return;
          const ok = await onDelete(toDelete);
          if (ok) setToDelete(null);
        }}
        onClose={() => setToDelete(null)}
      />
    </Card>
  );
};
